/* CyberpunkPhotoCard.module.css */

.card {
  position: relative;
  background: var(--bg-surface);
  border: 1px solid var(--neon-cyan);
  box-shadow: var(--glow-sm) var(--neon-cyan),
    inset 0 0 30px rgba(0, 240, 255, 0.03);
  padding: 16px;
  cursor: pointer;
  overflow: hidden;
  transition: box-shadow 0.3s ease, transform 0.2s ease;
  font-family: var(--font-mono);
  color: var(--text-primary);
}

.card:hover {
  box-shadow: var(--glow-md) var(--neon-cyan),
    inset 0 0 40px rgba(0, 240, 255, 0.05);
  transform: translateY(-2px);
}

.card[data-status="archived"] {
  border-color: var(--neon-magenta);
  box-shadow: 0 0 5px var(--neon-magenta), inset 0 0 30px rgba(255, 45, 149, 0.03);
}
.card[data-status="archived"]:hover {
  box-shadow: 0 0 10px var(--neon-magenta), 0 0 20px var(--neon-magenta), inset 0 0 40px rgba(255, 45, 149, 0.05);
}

.card[data-status="active"] {
  border-color: var(--neon-green);
  box-shadow: 0 0 5px var(--neon-green), inset 0 0 30px rgba(57, 255, 20, 0.03);
}
.card[data-status="active"]:hover {
  box-shadow: 0 0 10px var(--neon-green), 0 0 20px var(--neon-green), inset 0 0 40px rgba(57, 255, 20, 0.05);
}

/* Scanline moving animation */
.scanline {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(0, 240, 255, 0.2);
  z-index: 10;
  pointer-events: none;
  animation: scanMove 3s linear infinite;
}

/* Image wrapper */
.imageWrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border: 1px solid rgba(0, 240, 255, 0.3);
  margin-bottom: 12px;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: saturate(0.7) contrast(1.2);
  transition: filter 0.3s ease;
}

.card:hover .image {
  filter: saturate(0.9) contrast(1.3);
}

/* RGB glitch layer */
.glitchLayer {
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    0deg,
    transparent 0px,
    transparent 2px,
    rgba(0, 240, 255, 0.03) 2px,
    rgba(0, 240, 255, 0.03) 4px
  );
  mix-blend-mode: overlay;
  pointer-events: none;
}

.glitchLayer::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 0, 255, 0.1) 0%,
    rgba(0, 255, 255, 0.1) 100%
  );
  mix-blend-mode: color;
  pointer-events: none;
}

/* CRT scanlines overlay on image */
.crtOverlay {
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.15) 0px,
    rgba(0, 0, 0, 0.15) 1px,
    transparent 1px,
    transparent 2px
  );
  pointer-events: none;
}

/* Data panel */
.dataPanel {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dataRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.label {
  font-size: 10px;
  color: var(--text-muted);
  letter-spacing: 0.1em;
  font-family: var(--font-mono);
}

.value {
  font-size: 11px;
  color: var(--text-data);
  font-family: var(--font-mono);
  letter-spacing: 0.05em;
}

.threatBar {
  font-size: 10px;
  letter-spacing: -0.05em;
  color: var(--neon-cyan);
}

.threatPercent {
  font-size: 10px;
  color: var(--text-muted);
  margin-left: 4px;
}

/* Status colors */
.active {
  color: var(--neon-green) !important;
}

.archived {
  color: var(--neon-magenta) !important;
}

.indexed {
  color: var(--neon-cyan) !important;
}

/* Pulse animation */
.pulse {
  animation: pulse 2s ease-in-out infinite;
}

/* Blinking cursor */
.blink {
  animation: blink 1s step-end infinite;
}

/* Barcode footer */
.barcode {
  display: flex;
  justify-content: center;
  gap: 2px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 240, 255, 0.1);
}

.bar {
  display: inline-block;
  width: 3px;
  height: 10px;
  background: var(--text-muted);
  opacity: 0.6;
}

/* Hover glitch effect on image */
.card:hover .glitchLayer::before {
  animation: glitch 0.4s infinite;
}

/* Keyframes */
@keyframes scanMove {
  from { top: 0; }
  to { top: 100%; }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

@keyframes glitch {
  0% { clip-path: inset(40% 0 61% 0); transform: translate(-2px, 0); }
  20% { clip-path: inset(92% 0 1% 0); transform: translate(2px, 0); }
  40% { clip-path: inset(43% 0 1% 0); transform: translate(-1px, 0); }
  60% { clip-path: inset(25% 0 58% 0); transform: translate(1px, 0); }
  80% { clip-path: inset(54% 0 7% 0); transform: translate(-2px, 0); }
  100% { clip-path: inset(58% 0 43% 0); transform: translate(0); }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .scanline,
  .pulse,
  .blink {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
  .card:hover .glitchLayer::before {
    animation: none;
  }
  .card:hover {
    transform: none;
  }
}
